import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  Error,
  Icon,
  Status,
  Switch,
  Tooltip,
  Typography,
} from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { EnrollmentPreview } from 'widgets/ContactEnrollment/ui/EnrollmentList'
import { SendFrom } from 'widgets/SendFrom'
import { ENROLL_BY_WEBHOOK_TOOLTIP } from 'widgets/EnrollByWebhook/text.ts'
import { EnrollByWebhook } from 'widgets/EnrollByWebhook/EnrollByWebhook.tsx'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import styles from './styles.module.scss'

export const TriggerSection = observer(() => {
  const { triggerSectionStore } = useUnifiedAgentContext()
  const {
    runAutomatically,
    enrollByWebhookStore,
    sendFromError,
    enrollmentError,
    clearSendFromError,
    enrollmentFilters,
    sendFromStore,
    setRunAutomatically,
    editEnrollment,
    allowReEntering,
    setAllowReEntering,
    isShowContactsAlert,
    onCloseContactsAlert,
  } = triggerSectionStore

  return (
    <div>
      <Box display={'flex'} flexDirection={'column'} flexGrow={'1'}>
        <Typography variant={'body-lg-medium'} ariaLabel={'Trigger'}>
          Trigger
        </Typography>
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'TriggerDesc'}
          color={'var(--content-primary-secondary)'}
          marginTop={4}
          marginBottom={22}
        >
          Run this agent manually or automatically based on contact criteria
        </Typography>
      </Box>

      <div className={styles.checkboxGroup}>
        <div className={classnames(styles.checkboxRow, styles.disabled)}>
          <Checkbox checked={true} disabled ariaLabel={'RunManually'} size={16} />
          <Box display={'flex'} gap={'4px'} alignItems={'center'}>
            <Typography variant={'body-md-regular'} ariaLabel={'RunManuallyLabel'}>
              Run manually
            </Typography>
            <Tooltip
              label='Start this agent manually from:'
              desc={
                <>
                  Conversations
                  <br />
                  Contacts
                  <br />
                  Call flow
                  <br />
                  Broadcasts
                  <br />
                  Workflows
                </>
              }
              type='description'
              placement={'top'}
              breakWord
            >
              <Icon icon={'question'} color={'var(--content-neutral-primary)'} />
            </Tooltip>
          </Box>
        </div>
        <label
          className={classnames(styles.checkboxRow, { [styles.checked]: runAutomatically })}
          htmlFor={'runAutomaticallyCheckbox'}
        >
          <Checkbox
            id={'runAutomaticallyCheckbox'}
            checked={runAutomatically}
            onChange={setRunAutomatically}
            ariaLabel={'RunAutomatically'}
            size={16}
            className={styles.checkboxControl}
          />
          <Box display={'flex'} gap={'4px'} alignItems={'center'}>
            <Typography variant={'body-md-regular'} ariaLabel={'RunAutomaticallyLabel'}>
              Run automatically
            </Typography>
            <Tooltip
              label='The agent runs automatically when contacts match the enrollment criteria'
              placement={'top'}
              breakWord
            >
              <Icon icon={'question'} color={'var(--content-neutral-primary)'} />
            </Tooltip>
          </Box>
        </label>
        <label
          className={classnames(styles.checkboxRow, {
            [styles.checked]: enrollByWebhookStore.isActive,
          })}
          htmlFor={'runViaWebhook'}
        >
          <Checkbox
            id={'runViaWebhook'}
            checked={enrollByWebhookStore.isActive}
            onChange={enrollByWebhookStore.setActive}
            ariaLabel={'runViaWebhook'}
            size={16}
            className={styles.checkboxControl}
          />
          <Box display={'flex'} gap={'4px'} flexGrow={1} alignItems={'center'}>
            <Typography variant={'body-md-regular'} ariaLabel={'RunAutomaticallyLabel'}>
              Run via webhook
            </Typography>
            <Tooltip label={ENROLL_BY_WEBHOOK_TOOLTIP} placement={'top'} breakWord>
              <Icon icon={'questionFull'} padding={2} color={'var(--content-neutral-primary)'} />
            </Tooltip>
          </Box>
          <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />
        </label>
      </div>

      {runAutomatically && (
        <>
          <Typography
            variant={'text-input-label-small'}
            ariaLabel={'EnrollContactsLabel'}
            marginBottom={8}
            color={'var(--content-primary-secondary)'}
            tag='div'
          >
            Enroll contacts
          </Typography>
          <TextFieldPlaceholder
            className={styles.enrollField}
            variant='stroke'
            size='small'
            onClick={editEnrollment}
            error={enrollmentError}
            placeholder={
              <div className={styles.preview}>
                {enrollmentFilters.allSize ? (
                  <EnrollmentPreview filters={enrollmentFilters} />
                ) : (
                  'Select criteria'
                )}
              </div>
            }
          />
          <Error type={'text'} error={enrollmentError} />
          {isShowContactsAlert && (
            <Alert
              item={{
                type: 'warning',
                title: 'Contacts may re-enroll repeatedly',
                desc: 'Consider adjusting your filter or turning off re-enrollment',
              }}
              onClose={onCloseContactsAlert}
              className={styles.contactsReEnrollAlert}
            />
          )}
          <div
            className={styles.allowReEnroll}
            onClick={() => setAllowReEntering(!allowReEntering)}
          >
            <Typography variant={'body-md-regular'} ariaLabel={'ReEnrollLabel'}>
              Allow contacts to re-enroll after completion
            </Typography>
            <Switch value={allowReEntering} onChange={setAllowReEntering} size='small' />
          </div>
          <Divider style={{ marginBlock: '24px' }} />
        </>
      )}

      {enrollByWebhookStore.isActive && (
        <>
          <EnrollByWebhook
            store={enrollByWebhookStore}
            source={'agent'}
            specificSource={'AI Agent'}
            isHideTrigger
          />
          <Divider style={{ marginBlock: '24px' }} />
        </>
      )}

      <Box display={'flex'} flexDirection={'column'}>
        <Typography
          variant={'text-input-label-small'}
          ariaLabel={'StartConversationsFromLabel'}
          marginBottom={8}
          color={'var(--content-primary-secondary)'}
        >
          Start conversations from
        </Typography>
        <SendFrom
          store={sendFromStore}
          triggerVariant={'textField'}
          width={454}
          onClearError={clearSendFromError}
          textFiledTriggerProps={{
            error: { message: sendFromError },
            textFieldPlaceholderProps: {
              variant: 'stroke',
              size: 'medium',
              className: styles.textField,
            },
            rightActions: [makeTextFieldIcon({ icon: 'chevronDown' })],
          }}
        />
        <Typography
          variant={'text-input-helper'}
          ariaLabel={'InboxHint'}
          color={'var(--content-primary-secondary)'}
          marginTop={8}
        >
          To trigger the agent from incoming messages, use enrollment criteria
        </Typography>
      </Box>
    </div>
  )
})
